/**
 * src/api/scraping.js
 * Funciones para trigger del pipeline y consulta de estado/logs.
 */
import api from './client'

export const getEstado = (clienteId) =>
  api.get(`/scraping/${clienteId}/estado`).then(r => r.data)

export const getLogs = (clienteId, limit = 20) =>
  api.get(`/scraping/${clienteId}/logs`, { params: { limit } }).then(r => r.data)

export const triggerPipeline = (clienteId, tipo = 'ambos', dryRun = false) =>
  api.post(`/scraping/${clienteId}/trigger`, null, {
    params: { tipo, dry_run: dryRun },
  }).then(r => r.data)

export const getSchedulerJobs = () =>
  api.get('/scraping/scheduler/jobs').then(r => r.data)

// Cola de trabajos del pipeline (modo Railway: el panel encola y el WORKER
// ejecuta). Sin esto el panel decía "Job encolado" y ya: si el worker estaba
// caído o el tenant tenía otro job corriendo, no había forma de verlo desde
// aquí y la espera era ciega.
export const getPipelineJobs = (clienteId, limit = 8) =>
  api.get(`/scraping/${clienteId}/pipeline/jobs`, { params: { limit } }).then(r => r.data)

// Diagnóstico del embudo OCs → catálogo (admin, read-only).
export const getDiagnosticoCatalogo = (clienteId) =>
  api.get(`/scraping/${clienteId}/diagnostico-catalogo`).then(r => r.data)

// Censo de convenios marco del tenant, vía la API OCDS pública. `CodigoLicitacion`
// no sirve para esto (llega en 26 de 580 OCs y trae licitaciones ordinarias), así
// que hay que resolver orden a orden. Es REANUDABLE: cada llamada resuelve un tramo
// y cachea, por eso el timeout es amplio pero no infinito.
export const getCensoOcds = (clienteId, maxOcs = 150) =>
  api.get(`/scraping/${clienteId}/censo-ocds`, {
    params: { max_ocs: maxOcs },
    timeout: 300_000,
  }).then(r => r.data)

// ¿En qué tienda del portal vive cada convenio `sin_mapear` del censo? Ese cubo
// no es dinero cubierto ni perdido: es dinero del que nadie ha preguntado nunca
// ($491,7 M en manquehue, el 12,1 %). Read-only: NO escribe en licitacion_tienda.
// Hasta 2 peticiones por (código × tienda) más una por tienda → timeout amplio.
export const getSondaConvenios = (clienteId, opciones = {}) =>
  api.get(`/scraping/${clienteId}/sonda-convenios`, {
    params: {
      convenios: opciones.convenios || '',
      max_convenios: opciones.maxConvenios ?? 4,
      max_codigos: opciones.maxCodigos ?? 2,
      // Cada pulsación avanza a convenios nuevos y el resumen se funde con lo ya
      // guardado; `rehacer` es la única forma de refrescar una respuesta vieja.
      rehacer: opciones.rehacer ? 'true' : 'false',
    },
    timeout: 600_000,
  }).then(r => r.data)

// Qué publica una ficha y en qué paso se atasca nuestro lector. `parsear_ficha`
// falla en cinco sitios y devuelve el mismo null para todos; esto recorre los
// cinco. Sin URLs va a las tres de referencia (2 problemáticas + 1 control).
export const getSondaFicha = (urls = '') =>
  api.get('/scraping/admin/sonda-ficha', { params: { urls }, timeout: 180_000 })
    .then(r => r.data)

// Captura una ficha real para la red de foto fija de `parsear_ficha`. Hoy esa
// función no tiene ni un test contra HTML real, y por eso el encargo del
// multi-tienda está congelado. Devuelve el fixture listo para versionar.
// ⚠️ `codigo` NO es opcional en todas: en una tienda de DOS EJES —donde una misma
// ficha contiene varios productos lógicos— `parsear_ficha` devuelve `null` sin él,
// a propósito, para no quedarse con 16 de 48 códigos elegidos al azar. Sin este
// parámetro la captura «funcionaba» y devolvía un fixture vacío, que es peor que
// fallar: se versionaría una red que no vigila nada (§5).
// ⚠️ Y `url` NO es un lujo de los dos: el código elige el producto DENTRO de una
// ficha, no la ficha. Sin URL el backend descubre **una cualquiera** de la tienda,
// y en una tienda de varios productos por ficha eso significa buscar el código en
// la página equivocada. Medido el 21-09-2026 en `emergencias202109`: volvió la
// ficha de un arriendo de duchas con `esperado: null`. El parámetro existía desde
// el principio; lo que faltaba era el campo en pantalla que lo llenara.
export const getFotoFija = (tienda, url = '', codigo = '') =>
  api.get('/scraping/admin/foto-fija', { params: { tienda, url, codigo }, timeout: 120_000 })
    .then(r => r.data)

// Con qué URL y qué código se capturó cada foto ya versionada. Es lo que hace
// REHACIBLE la red: un fixture caduca cuando el portal cambia su HTML, y
// recapturar una tienda de varios productos por ficha exige su URL y su código
// — dos datos que vivían solo dentro del fixture, un blob comprimido de 300 KB
// en un fichero del repositorio. Instantáneo: no toca el portal, y no trae el
// HTML.
export const getFotoFijaVersionadas = () =>
  api.get('/scraping/admin/foto-fija/versionadas').then(r => r.data)

// Revalidación de las tiendas de Convenio Marco (admin, read-only). El survey
// multi-tienda es de junio y sus datos crudos no se commitearon, así que darlo
// por vigente sin volver a medir sería fiarse de una foto vieja. ~1+N peticiones
// por tienda → timeout amplio.
export const getSondaTiendas = (slugs = '', limite = 5) =>
  api.get('/scraping/admin/sonda-tiendas', {
    params: { slugs, limite },
    timeout: 180_000,
  }).then(r => r.data)

// Sonda de vetting de un proveedor (admin, read-only, no crea tenant). Muestrea
// sus OCs por la API MP; puede tardar (~1 request/día) → timeout amplio.
export const getSondaProveedor = (sellerCode, dias = 30) =>
  api.get('/scraping/admin/sonda-proveedor', {
    params: { seller_code: sellerCode, dias },
    timeout: 120_000,
  }).then(r => r.data)

// La cola de convenios atascados, SUMADA entre todos los tenants. No mide nada
// nuevo: lee los resúmenes que la sonda ya dejó guardados, así que es instantáneo
// y no toca el portal. Es lo que decide qué tienda del portal se porta después.
export const getColaConvenios = () =>
  api.get('/scraping/admin/cola-convenios').then(r => r.data)
